/**
 * How the player's own hand behaves: what order it sits in, and what a click
 * on a card actually selects.
 *
 * Both are presentation concerns, so they live here rather than in the engine.
 * Selection in particular is a convenience layer - it only ever produces sets
 * of cards the player could plausibly play together, and the server still
 * validates whatever is submitted.
 */

import { type Card, type Rank, type Suit, isThree, isWild } from "@ryzun/haf-saloon-game-engine";

/** Groups the hand: wilds first, then the ranks, then the threes. */
const BUCKET_JOKER = 0;

const BUCKET_TWO = 1;

const BUCKET_NATURAL = 2;

const BUCKET_BLACK_THREE = 3;

const BUCKET_RED_THREE = 4;

/** Ranks sort high to low within their bucket, ace down to four. */
const NATURAL_ORDER: Readonly<Record<Rank, number>> = {
    2: 0,
    3: 0,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 10,
    ace: 14,
    jack: 11,
    joker: 0,
    king: 13,
    queen: 12
};

/** Blacks before reds, so the two three buckets read the way they are described. */
const SUIT_ORDER: Readonly<Record<Suit, number>> = {
    spades: 0,
    clubs: 1,
    hearts: 2,
    diamonds: 3
};

function getBucket(card: Card): number {
    if (card.rank === "joker") {
        return BUCKET_JOKER;
    }

    if (card.rank === "2") {
        return BUCKET_TWO;
    }

    if (isThree(card)) {
        return card.color === "black" ? BUCKET_BLACK_THREE : BUCKET_RED_THREE;
    }

    return BUCKET_NATURAL;
}

/** A tiebreak so the same hand always lays out the same way. */
function getSuitOrder(card: Card): number {
    if (card.suit === null) {
        return card.color === "black" ? 0 : 1;
    }

    return SUIT_ORDER[card.suit];
}

/**
 * Jokers, then twos, then the ranks from ace down to four, then black threes and
 * finally red threes - the order they cost you if the round ends on them (§6).
 */
export function sortHand(cards: readonly Card[]): readonly Card[] {
    return cards.toSorted((left, right) => {
        const bucket = getBucket(left) - getBucket(right);

        if (bucket !== 0) {
            return bucket;
        }

        const rank = NATURAL_ORDER[right.rank] - NATURAL_ORDER[left.rank];

        return rank === 0 ? getSuitOrder(left) - getSuitOrder(right) : rank;
    });
}

function findCard(hand: readonly Card[], cardId: string): Card | undefined {
    return hand.find((card) => card.id === cardId);
}

function keepWilds(hand: readonly Card[], selected: readonly string[]): readonly string[] {
    return selected.filter((id) => {
        const card = findCard(hand, id);
        return card !== undefined && isWild(card);
    });
}

function dropThrees(hand: readonly Card[], selected: readonly string[]): readonly string[] {
    return selected.filter((id) => {
        const card = findCard(hand, id);
        return card !== undefined && !isThree(card);
    });
}

function toggleOne(selected: readonly string[], cardId: string): readonly string[] {
    return selected.includes(cardId)
        ? selected.filter((id) => id !== cardId)
        : [...selected, cardId];
}

/**
 * What clicking a card selects.
 *
 * A meld is a rank plus optional wilds, so picking a rank picks every copy of it
 * and drops whatever other rank was held, while wilds toggle one at a time and
 * survive a change of rank. Threes can only ever be discarded, so choosing one
 * clears everything else rather than pretending it could join a meld.
 *
 * The same rules apply before the team is in the game: an opener is assembled
 * one meld at a time onto the team's own row, so each of those melds is picked
 * exactly the way any other meld is.
 */
export function toggleSelection(
    hand: readonly Card[],
    selected: readonly string[],
    cardId: string
): readonly string[] {
    const card = findCard(hand, cardId);

    if (card === undefined) {
        return selected;
    }

    if (isThree(card)) {
        return selected.includes(cardId) ? selected.filter((id) => id !== cardId) : [cardId];
    }

    // A three can only ever be discarded, so it is dropped the moment anything
    // that could be melded is picked up alongside it.
    if (isWild(card)) {
        return toggleOne(dropThrees(hand, selected), cardId);
    }

    // Picking a rank takes every copy of it, but dropping one drops only that
    // card, so a meld can be trimmed down without rebuilding the selection.
    if (selected.includes(cardId)) {
        return selected.filter((id) => id !== cardId);
    }

    const sameRank = hand.filter((entry) => entry.rank === card.rank).map((entry) => entry.id);

    return [...keepWilds(hand, selected), ...sameRank];
}
